import { useMemo, useState } from "react";
import { X, Search, Check } from "@semo/icons";
import { MATERIAL_DB, LABOR_DB, type MaterialDbEntry } from "../materialDb";
import { formatWon } from "../calc";

interface DbSearchModalProps {
  open: boolean;
  onClose: () => void;
  /** 자재 DB / 시공·인건 DB 구분 */
  kind: "material" | "labor";
  /** 선택한 항목들을 현재 공사에 추가 */
  onAdd: (
    items: {
      name: string;
      unit: string;
      quantity: number;
      unitPrice: number;
    }[],
  ) => void;
}

/** DB 검색 팝업 — 검색/다중선택 후 품목으로 추가 (자재 / 시공·인건) */
export function DbSearchModal({
  open,
  onClose,
  kind,
  onAdd,
}: DbSearchModalProps) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const catalog = kind === "labor" ? LABOR_DB : MATERIAL_DB;
  const isLabor = kind === "labor";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q),
    );
  }, [query, catalog]);

  if (!open) return null;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    const picked = catalog.filter((m) => selected.has(m.id));
    if (picked.length === 0) return;
    onAdd(
      picked.map((m: MaterialDbEntry) => ({
        name: m.name,
        unit: m.unit,
        quantity: 1,
        unitPrice: m.unitPrice,
      })),
    );
    setSelected(new Set());
    setQuery("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {isLabor ? "시공·인건 DB 검색" : "자재 DB 검색"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="닫기"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 검색 */}
        <div className="shrink-0 px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 focus-within:border-primary">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                isLabor
                  ? "공종 또는 시공명으로 검색 (예: 목공, 타일, 도배)"
                  : "자재명 또는 분류로 검색 (예: 창호, 타일)"
              }
              className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">
            {isLabor
              ? "단가는 단위 1 기준입니다 (예: 1m²·1평·1시간). 추가 시 수량 1로 들어가며, 이후 수량/단가를 수정할 수 있어요."
              : "단가는 단위 1 기준입니다 (예: 1개·1m²·1평). 추가 시 수량 1로 들어가며, 이후 수량/단가를 수정할 수 있어요."}
          </p>
        </div>

        {/* 목록 */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="w-10 py-2" />
                <th className="py-2 text-left font-semibold">
                  {isLabor ? "시공명" : "자재명"}
                </th>
                <th className="w-20 py-2 text-left font-semibold">분류</th>
                <th className="w-16 py-2 text-center font-semibold">단위</th>
                <th className="w-28 py-2 text-right font-semibold">단가</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const checked = selected.has(m.id);
                return (
                  <tr
                    key={m.id}
                    onClick={() => toggle(m.id)}
                    className={`cursor-pointer border-b border-slate-100 transition-colors ${
                      checked ? "bg-primary/5" : "hover:bg-slate-50"
                    }`}
                  >
                    <td className="py-2 text-center">
                      <span
                        className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                          checked
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-slate-300"
                        }`}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                    </td>
                    <td className="py-2 text-slate-800">{m.name}</td>
                    <td className="py-2 text-slate-500">{m.category}</td>
                    <td className="py-2 text-center text-slate-600">{m.unit}</td>
                    <td className="py-2 text-right tabular-nums text-slate-800">
                      {formatWon(m.unitPrice)}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center text-sm text-slate-400"
                  >
                    검색 결과가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 푸터 */}
        <div className="flex shrink-0 items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-500">
            {selected.size}개 선택됨
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={selected.size === 0}
              className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              선택 추가
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
